import {useEffect,useRef,useState} from 'react';
import {KeyRound} from 'lucide-react';
import {isTauri,invoke} from '@tauri-apps/api/core';
import {listen} from '@tauri-apps/api/event';
import {Button} from '@/components/ui/Button';
import {signInWithPasskey,browserSupportsWebAuthn,passkeyError,startDesktopPasskey,finishDesktopPasskey,handoffPasskeyToDesktop,DESKTOP_PASSKEY_PARAM} from './passkeys';
import type {AuthSession} from './api';
interface DesktopRoute {host:string;query?:string|Record<string,string>|null}
/** Set when the desktop app opened this page in the system browser to verify a passkey for it. */
function desktopHandoffHash():string|null {
  const value=new URLSearchParams(window.location.search).get(DESKTOP_PASSKEY_PARAM);
  return value&&/^[0-9a-f]{64}$/.test(value)?value:null;
}
export function PasskeySignIn({onVerified,disabled=false}:{onVerified:(session:AuthSession)=>void;disabled?:boolean}) {
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[notice,setNotice]=useState('');
  // The desktop webview (WKWebView / WebKitGTK / WebView2) rejects WebAuthn unless the app is associated with
  // chat.aile-ai.jp (webcredentials), which the macOS builds from 0.1.2 / Mac App Store build 3 are; others verify in the system browser.
  const desktop=isTauri(),handoff=desktop?null:desktopHandoffHash();
  const inApp=desktop&&navigator.userAgent.includes('AileChatWebCredentials')&&/^Mac/.test(navigator.platform);
  const [fallback,setFallback]=useState(false);
  const verified=useRef(onVerified);verified.current=onVerified;
  useEffect(()=>{
    if(!desktop)return;
    let disposed=false;const stops:(()=>void)[]=[];
    for(const scheme of ['ailechat','kakehashi'])void listen<DesktopRoute>(`${scheme}://route`,event=>{
      if(event.payload.host!=='auth-callback')return;
      const code=new URLSearchParams(typeof event.payload.query==='string'?event.payload.query:event.payload.query||{}).get('code');
      if(!code)return;
      setBusy(true);setError('');setNotice('');
      finishDesktopPasskey(code).then(session=>verified.current(session),error=>setError(passkeyError(error))).finally(()=>setBusy(false));
    }).then(stop=>{if(disposed)stop();else stops.push(stop);},()=>{});
    return ()=>{disposed=true;stops.forEach(stop=>stop());};
  },[desktop]);
  const signIn=async(browser=desktop&&!inApp)=>{
    if(busy)return;setBusy(true);setError('');setNotice('');
    try{
      if(browser){
        await invoke('plugin:shell|open',{path:await startDesktopPasskey()});
        setNotice('ブラウザが開きます。Passkeyで確認すると、このアプリに戻ってログインします。');
      }else if(handoff){
        const callback=await handoffPasskeyToDesktop(handoff);
        setNotice('確認できました。Aile Chat アプリに戻ってください。');
        window.location.href=callback;
      }else onVerified(await signInWithPasskey());
    }catch(error){setError(passkeyError(error));if(inApp)setFallback(true);}finally{setBusy(false);}
  };
  if(!desktop&&!browserSupportsWebAuthn())return null;
  return <div className="mt-4 space-y-2">
    <Button type="button" variant="secondary" className="w-full" disabled={disabled||busy} leadingIcon={<KeyRound size={16} aria-hidden="true"/>} onClick={()=>void signIn()}>{busy?'Passkeyを確認しています…':handoff?'Passkeyで確認してアプリに戻る':'Passkeyでログイン'}</Button>
    {notice&&<p role="status" className="text-sm text-fg-secondary">{notice}</p>}
    {error&&<p role="alert" className="text-sm text-danger">{error}</p>}
    {inApp&&fallback&&!notice&&<button type="button" className="text-sm text-fg-secondary underline" disabled={busy} onClick={()=>void signIn(true)}>ブラウザでPasskeyを確認する</button>}
  </div>;
}
